import type { CardState, ChatAction, ChatEvent, ChatMessage, ErrorPayload, ResultCard, StreamStatus } from "./types";
import { withoutNoAnswerMarker } from "./no-answer-marker";
import type { WorkflowReply } from "./workflow/contract";
import { toArticleCard, withArticle } from "./workflow/article";
import { hasOpenWorkflowCard, runComposer, settleWorkflowCard } from "./workflow/step";

export interface ChatState {
  messages: ChatMessage[];
  status: StreamStatus;
  conversationId: string | null;
  error: ErrorPayload | null;
  /** Assistant message currently receiving the stream. */
  activeId: string | null;
}

export type ChatStateAction =
  | { type: "start"; assistantId: string; user?: { id: string; text: string; workflowReply?: WorkflowReply } }
  | { type: "status"; status: StreamStatus }
  | { type: "event"; event: ChatEvent }
  | { type: "tokens"; text: string }
  | { type: "fail"; error: ErrorPayload; unsent: boolean }
  | { type: "stop" }
  | { type: "truncate"; fromId: string }
  | { type: "append"; message: ChatMessage }
  /** A person took over: the workflow's open questions can no longer be answered. */
  | { type: "settleCards" }
  /** Assistant messages that exist before any turn (a started workflow run); ones already present are skipped. */
  | { type: "insert"; messages: ChatMessage[] }
  /** A booking / lead card turned into its result (`succeeded`) or can no longer be used (`expired`, `unavailable`). */
  | { type: "actionState"; messageId: string; state: CardState; result?: ResultCard }
  | { type: "reset"; messages?: ChatMessage[]; conversationId?: string | null };

export function createMessage(id: string, role: ChatMessage["role"], text = "", status: ChatMessage["status"] = "done"): ChatMessage {
  return { id, role, text, status, citations: [], followups: [] };
}

function userMessage(user: { id: string; text: string; workflowReply?: WorkflowReply }): ChatMessage {
  const message = createMessage(user.id, "user", user.text);
  return user.workflowReply ? { ...message, workflowReply: user.workflowReply } : message;
}

export function initialChatState(messages: ChatMessage[] = [], conversationId: string | null = null): ChatState {
  return { messages, status: "idle", conversationId, error: null, activeId: null };
}

function patchActive(state: ChatState, patch: (message: ChatMessage) => ChatMessage): ChatMessage[] {
  return state.messages.map((m) => (m.id === state.activeId ? patch(m) : m));
}

function applyEvent(state: ChatState, event: ChatEvent): ChatState {
  switch (event.type) {
    case "meta":
      return { ...state, conversationId: event.data.conversation_id, messages: patchActive(state, (m) => ({ ...m, serverId: event.data.message_id })) };
    case "status":
      return { ...state, status: state.status === "polling" ? "polling" : "streaming", messages: patchActive(state, (m) => ({ ...m, stage: event.data.stage })) };
    case "token":
      return applyTokens(state, event.data.text);
    case "citations":
      return { ...state, messages: patchActive(state, (m) => ({ ...m, citations: event.data })) };
    case "action": {
      const article = toArticleCard(event.data);
      return { ...state, messages: patchActive(state, (m) => (article ? withArticle(m, article) : { ...m, action: event.data })) };
    }
    case "revise":
      return { ...state, messages: patchActive(state, (m) => ({ ...m, text: withoutNoAnswerMarker(event.data.text) })) };
    case "done":
      return {
        ...state,
        status: "idle",
        activeId: null,
        messages: patchActive(state, (m) => ({
          ...m,
          status: "done",
          followups: event.data.followups,
          gap: event.data.gap,
          grounded: event.data.grounded,
          confidence: event.data.confidence,
          ...(event.data.workflow
            ? { workflowRunId: event.data.workflow.run_id, workflowComposer: runComposer(event.data.workflow) }
            : {}),
          ...(event.data.workflow_message ? { workflowMessage: true } : {}),
        })),
      };
    case "error":
      return {
        ...state,
        status: "error",
        error: event.data,
        activeId: null,
        messages: patchActive(state, (m) => ({ ...m, status: "error", error: event.data })),
      };
  }
}

function applyTokens(state: ChatState, text: string): ChatState {
  if (!text) return state;
  return {
    ...state,
    status: state.status === "polling" ? "polling" : "streaming",
    messages: patchActive(state, (m) => ({ ...m, text: withoutNoAnswerMarker(m.text + text) })),
  };
}

function failActive(state: ChatState, error: ErrorPayload, unsent: boolean): ChatState {
  const index = state.messages.findIndex((m) => m.id === state.activeId);
  if (index === -1) return { ...state, status: "error", error, activeId: null };
  const active = state.messages[index];
  const hasContent = Boolean(active?.text);
  let messages: ChatMessage[];
  if (unsent && !hasContent) {
    // Nothing arrived: drop the placeholder and mark the question "Not sent · Tap to retry".
    messages = state.messages.filter((m) => m.id !== state.activeId);
    const lastUser = messages.findLastIndex((m) => m.role === "user");
    messages = messages.map((m, i) => (i === lastUser ? { ...m, status: "unsent", error } : m));
  } else {
    messages = state.messages.map((m) => (m.id === state.activeId ? { ...m, status: "error", error } : m));
  }
  return { ...state, status: "error", error, activeId: null, messages };
}

export function chatReducer(state: ChatState, action: ChatStateAction): ChatState {
  switch (action.type) {
    case "start": {
      const { user } = action;
      const added = user ? [userMessage(user)] : [];
      const earlier = user ? settleWorkflowCards(state.messages, user.text, user.workflowReply) : state.messages;
      return {
        ...state,
        status: "connecting",
        error: null,
        activeId: action.assistantId,
        messages: [...earlier, ...added, createMessage(action.assistantId, "assistant", "", "streaming")],
      };
    }
    case "status":
      return { ...state, status: action.status };
    case "event":
      return applyEvent(state, action.event);
    case "tokens":
      return applyTokens(state, action.text);
    case "fail":
      return failActive(state, action.error, action.unsent);
    case "stop":
      if (!state.activeId) return { ...state, status: "idle" };
      return { ...state, status: "idle", activeId: null, messages: patchActive(state, (m) => ({ ...m, status: "stopped" })) };
    case "truncate": {
      const index = state.messages.findIndex((m) => m.id === action.fromId);
      return index === -1 ? state : { ...state, error: null, messages: state.messages.slice(0, index) };
    }
    case "append": {
      if (hasMessage(state, action.message)) return state;
      // A team member's reply ends the workflow run on the server: its open cards close here too.
      const earlier = action.message.role === "staff" ? settleOpenCards(state.messages) : state.messages;
      return { ...state, messages: [...earlier, action.message] };
    }
    case "settleCards":
      return { ...state, messages: settleOpenCards(state.messages) };
    case "insert": {
      const fresh = action.messages.filter((message) => !hasMessage(state, message));
      return fresh.length === 0 ? state : { ...state, messages: [...state.messages, ...fresh] };
    }
    case "actionState":
      return { ...state, messages: state.messages.map((m) => (m.id === action.messageId || m.serverId === action.messageId ? withCardState(m, action.state, action.result) : m)) };
    case "reset":
      return initialChatState(action.messages ?? [], action.conversationId ?? null);
  }
}

/** The message with its card moved to `state` (a result is kept once set); other actions are untouched. */
function withCardState(message: ChatMessage, state: CardState, result: ResultCard | undefined): ChatMessage {
  const action = message.action;
  if (action?.type !== "book_tour" && action?.type !== "lead_form") return message;
  const next: ChatAction =
    action.type === "book_tour"
      ? { type: "book_tour", payload: { ...action.payload, state, result: result ?? action.payload.result } }
      : { type: "lead_form", payload: { ...action.payload, state, result: result ?? action.payload.result } };
  return { ...message, action: next };
}

/**
 * The visitor sent something: every open workflow card is settled (answered), and the one the
 * message answers keeps what was picked or typed so it can show the choice.
 */
function settleWorkflowCards(messages: ChatMessage[], text: string, reply: WorkflowReply | undefined): ChatMessage[] {
  if (!messages.some(hasOpenWorkflowCard)) return messages;
  return messages.map((message) => {
    if (!hasOpenWorkflowCard(message)) return message;
    const { payload } = message.action;
    const answered = reply?.run_id === payload.run_id && reply.step_id === payload.step_id;
    return settleWorkflowCard(message, answered ? { button_id: reply.button_id, value: reply.value, label: text } : undefined);
  });
}

/** Every open workflow card settled without an answer (a closed question is shown as such). */
function settleOpenCards(messages: ChatMessage[]): ChatMessage[] {
  return messages.some(hasOpenWorkflowCard) ? messages.map((message) => (hasOpenWorkflowCard(message) ? settleWorkflowCard(message) : message)) : messages;
}

/** A message that arrived outside a turn (staff reply) may already be in the restored transcript. */
function hasMessage(state: ChatState, message: ChatMessage): boolean {
  const known = message.serverId ?? message.id;
  return state.messages.some((m) => m.id === known || m.serverId === known);
}

/** The assistant message whose citations/action should drive side panels. */
export function latestAssistant(state: ChatState): ChatMessage | undefined {
  if (state.activeId) return state.messages.find((m) => m.id === state.activeId);
  return state.messages.findLast((m) => m.role === "assistant");
}
